import React, { useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FaGraduationCap, FaShoppingCart, FaHeartbeat, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Case, StyledComponentProps, AnimationVariants } from '../types';
import { useLanguage } from '../i18n';

const CasesContainer = styled.section`
  padding: 100px 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
`;

const CaseCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.borderLight};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const CaseImage = styled.div<StyledComponentProps & { $coverImage?: string; $imageError?: boolean }>`
  height: 200px;
  background: ${props => {
    if (props.$imageError || !props.$coverImage) {
      return props.gradient || props.theme.gradients.primary;
    }
    return `url(${props.$coverImage}) center/cover`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  color: white;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => {
      if (props.$imageError || !props.$coverImage) {
        return 'url(data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><radialGradient id="a" cx="50%" cy="50%"><stop offset="0%" stop-color="%23ffffff" stop-opacity="0.1"/><stop offset="100%" stop-color="%23ffffff" stop-opacity="0"/></radialGradient></defs><circle cx="200" cy="200" r="100" fill="url(%23a)"/><circle cx="800" cy="300" r="150" fill="url(%23a)"/></svg>)';
      }
      return 'rgba(0, 0, 0, 0.3)';
    }};
    opacity: 0.3;
  }
  
  .icon-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
  
  .image-error {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 3;
  }
`;

const CaseContent = styled.div`
  padding: 32px;
`;

const CaseTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 16px;
`;

const CaseDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const CaseStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 16px;
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: 12px;
  
  .stat-number {
    font-size: 1.5rem;
    font-weight: 800;
    color: ${props => props.theme.colors.primary};
    display: block;
  }
  
  .stat-label {
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textLight};
  }
`;

const CaseButton = styled(motion.button)`
  background: ${props => props.theme.gradients.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

// Gallery Modal Styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalImage = styled(motion.img)`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
`;

const ModalControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
  color: white;
`;

const ModalButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 10px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled(ModalButton)`
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
`;

const ImageCounter = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 500;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 10px;
  margin-top: 15px;
  max-width: 400px;
`;

const Thumbnail = styled.img<{ $active: boolean }>`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: ${props => props.$active ? '3px solid #667eea' : '2px solid transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    transform: scale(1.05);
  }
`;

interface CasesProps {
  id?: string;
}

const Cases: React.FC<CasesProps> = ({ id }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  
  // Gallery state
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Image loading state
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  // const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  const cases: Case[] = React.useMemo(() => [
      {
        icon: <FaGraduationCap />,
        title: t('case.auto.title'),
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        description: t('case.auto.desc'),
        stats: [
          { number: "85%", label: t('case.auto.stat1') },
          { number: "70%", label: t('case.auto.stat2') }
        ],
      coverImage: "/images/cases/case1/slide_03.png",
      images: [
        "/images/cases/case1/slide_03.png",
        "/images/cases/case1/slide_04.png",
        "/images/cases/case1/slide_05.png",
        "/images/cases/case1/slide_06.png",
        "/images/cases/case1/slide_07.png",
        "/images/cases/case1/slide_08.png",
        "/images/cases/case1/slide_09.png",
        "/images/cases/case1/slide_10.png"
      ]
    },
      {
        icon: <FaShoppingCart />,
        title: t('case.legal.title'),
        gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        description: t('case.legal.desc'),
        stats: [
          { number: "90%", label: t('case.legal.stat1') },
          { number: "95%", label: t('case.legal.stat2') }
        ],
      coverImage: "/images/cases/case2/slide_11.png",
      images: [
        "/images/cases/case2/slide_11.png",
        "/images/cases/case2/slide_12.png",
        "/images/cases/case2/slide_13.png",
        "/images/cases/case2/slide_14.png",
        "/images/cases/case2/slide_15.png",
        "/images/cases/case2/slide_16.png",
        "/images/cases/case2/slide_17.png"
      ]
    },
      {
        icon: <FaHeartbeat />,
        title: t('case.insurance.title'),
        gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        description: t('case.insurance.desc'),
        stats: [
          { number: "75%", label: t('case.insurance.stat1') },
          { number: "60%", label: t('case.insurance.stat2') }
        ],
      coverImage: "/images/cases/case3/slide_18.png",
      images: [
        "/images/cases/case3/slide_18.png",
        "/images/cases/case3/slide_19.png",
        "/images/cases/case3/slide_20.png",
        "/images/cases/case3/slide_21.png",
        "/images/cases/case3/slide_22.png",
        "/images/cases/case3/slide_23.png"
      ]
    }
  ], [t]);

  const containerVariants: AnimationVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants: AnimationVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Gallery functions
  const openGallery = (caseIndex: number) => {
    setCurrentCaseIndex(caseIndex);
    setCurrentImageIndex(0);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const nextImage = useCallback(() => {
    const currentCase = cases[currentCaseIndex];
    if (currentCase.images) {
      setCurrentImageIndex((prev) => 
        prev < currentCase.images!.length - 1 ? prev + 1 : 0
      );
    }
  }, [currentCaseIndex, cases]);

  const prevImage = useCallback(() => {
    const currentCase = cases[currentCaseIndex];
    if (currentCase.images) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : currentCase.images!.length - 1
      );
    }
  }, [currentCaseIndex, cases]);

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Image loading handlers
  const handleImageLoad = (imageSrc: string) => {
    // setLoadedImages(prev => new Set(prev).add(imageSrc));
    setImageErrors(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageSrc);
      return newSet;
    });
  };

  const handleImageError = (imageSrc: string) => {
    setImageErrors(prev => new Set(prev).add(imageSrc));
    // setLoadedImages(prev => {
    //   const newSet = new Set(prev);
    //   newSet.delete(imageSrc);
    //   return newSet;
    // });
  };

  // Preload images
  React.useEffect(() => {
    const preloadImages = () => {
      cases.forEach(caseItem => {
        if (caseItem.images) {
          caseItem.images.forEach(imageSrc => {
            const img = new Image();
            img.onload = () => handleImageLoad(imageSrc);
            img.onerror = () => handleImageError(imageSrc);
            img.src = imageSrc;
          });
        }
      });
    };

    if (isInView) {
      preloadImages();
    }
  }, [isInView, cases]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isGalleryOpen) return;
    
    switch (e.key) {
      case 'Escape':
        closeGallery();
        break;
      case 'ArrowLeft':
        prevImage();
        break;
      case 'ArrowRight':
        nextImage();
        break;
    }
  }, [isGalleryOpen, prevImage, nextImage]);

  React.useEffect(() => {
    if (isGalleryOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isGalleryOpen, handleKeyDown]);

  return (
    <CasesContainer id={id}>
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            {t('cases.section.title')}
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('cases.section.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <CasesGrid>
            {cases.map((caseItem, index) => (
              <CaseCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CaseImage 
                  gradient={caseItem.gradient}
                  $coverImage={caseItem.coverImage}
                  $imageError={caseItem.coverImage ? imageErrors.has(caseItem.coverImage) : false}
                  onClick={() => caseItem.images && openGallery(index)}
                >
                  {caseItem.coverImage && imageErrors.has(caseItem.coverImage) && (
                    <div className="image-error">
                      {t('cases.image.failed')}
                    </div>
                  )}
                </CaseImage>
                <CaseContent>
                  <CaseTitle>{caseItem.title}</CaseTitle>
                  <CaseDescription>{caseItem.description}</CaseDescription>
                  <CaseStats>
                    {caseItem.stats.map((stat, statIndex) => (
                      <StatItem key={statIndex}>
                        <span className="stat-number">{stat.number}</span>
                        <span className="stat-label">{stat.label}</span>
                      </StatItem>
                    ))}
                  </CaseStats>
                  <CaseButton
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => caseItem.images ? openGallery(index) : undefined}
                  >
                    {caseItem.images ? t('cases.view.images') : t('cases.view.detail')}
                  </CaseButton>
                </CaseContent>
              </CaseCard>
            ))}
          </CasesGrid>
        </motion.div>
      </Container>

      {/* Gallery Modal */}
      <AnimatePresence>
        {isGalleryOpen && cases[currentCaseIndex].images && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeGallery}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={closeGallery}>
                <FaTimes />
              </CloseButton>
              
              <ModalImage
                src={cases[currentCaseIndex].images![currentImageIndex]}
                alt={`${cases[currentCaseIndex].title} - ${currentImageIndex + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onLoad={() => handleImageLoad(cases[currentCaseIndex].images![currentImageIndex])}
                onError={() => handleImageError(cases[currentCaseIndex].images![currentImageIndex])}
              />
              
              <ModalControls>
                <ModalButton onClick={prevImage}>
                  <FaChevronLeft />
                </ModalButton>
                
                <ImageCounter>
                  {currentImageIndex + 1} / {cases[currentCaseIndex].images!.length}
                </ImageCounter>
                
                <ModalButton onClick={nextImage}>
                  <FaChevronRight />
                </ModalButton>
              </ModalControls>
              
              {cases[currentCaseIndex].images!.length > 1 && (
                <ThumbnailGrid>
                  {cases[currentCaseIndex].images!.map((image, index) => (
                    <Thumbnail
                      key={index}
                      src={image}
                      alt={`${t('cases.thumb.alt')} ${index + 1}`}
                      $active={index === currentImageIndex}
                      onClick={() => goToImage(index)}
                      onLoad={() => handleImageLoad(image)}
                      onError={() => handleImageError(image)}
                    />
                  ))}
                </ThumbnailGrid>
              )}
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </CasesContainer>
  );
};

export default Cases;
